import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiXCircle, FiPlay, FiFileText, FiClock } from 'react-icons/fi';

const ChallengeView = ({ challenge, onSubmit, feedback, submitting, challengeIndex, totalChallenges, attempt, chapterCompleted, challengeId }) => {
  // Inicializar com a resposta anterior se existir
  const [selectedAnswer, setSelectedAnswer] = useState(attempt?.selected_answer || '');
  const [essayAnswer, setEssayAnswer] = useState(attempt?.essay_answer || '');
  
  // Atualizar quando attempt mudar - SEMPRE que attempt mudar, atualizar os estados
  useEffect(() => {
    if (attempt) {
      if (challenge.type === 'multiple_choice') {
        if (attempt.selected_answer) {
          setSelectedAnswer(attempt.selected_answer);
        }
      }
      if (challenge.type === 'essay') {
        if (attempt.essay_answer) {
          setEssayAnswer(attempt.essay_answer);
        }
      }
    } else {
      // Resetar apenas se realmente não houver tentativa
      if (challenge.type === 'multiple_choice') {
        setSelectedAnswer('');
      } else {
        setEssayAnswer('');
      }
    }
  }, [attempt, challenge.type, challenge.id]);

  const handleSubmit = () => {
    if (challenge.type === 'essay') {
      if (!essayAnswer.trim()) {
        return;
      }
      onSubmit(essayAnswer, challenge.id);
    } else {
      if (!selectedAnswer) {
        return;
      }
      onSubmit(selectedAnswer, challenge.id);
    }
  };

  const isMultipleChoice = challenge.type === 'multiple_choice';
  const progress = totalChallenges > 0 ? ((challengeIndex + 1) / totalChallenges) * 100 : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              {isMultipleChoice ? `Desafio ${challengeIndex + 1}` : 'Questão Discursiva'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {isMultipleChoice 
                ? `Questão ${challengeIndex + 1} de ${totalChallenges}`
                : 'Esta questão será corrigida por um professor'}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            Progresso: {Math.round(progress)}%
          </div>
        </div>
        <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question Content */}
      <div className="p-6">
        <div className="mb-6">
          <p className="text-lg text-gray-800 font-medium leading-relaxed">
            {challenge.question}
          </p>
        </div>

        {/* Multiple Choice Options */}
        {isMultipleChoice && (
          <div className="space-y-3 mb-6">
            {/* Mostrar feedback se já foi respondido corretamente */}
            {attempt?.is_correct === 1 && (
              <div className="mb-4 p-4 rounded-lg bg-green-50 border-2 border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <FiCheckCircle className="text-green-600 text-xl" />
                  <h4 className="font-semibold text-green-800">Resposta Correta!</h4>
                </div>
                <p className="text-sm text-green-700">
                  Você já respondeu corretamente esta questão. Sua resposta foi: <strong>{attempt.selected_answer}</strong>
                </p>
              </div>
            )}
            
            {['A', 'B', 'C', 'D'].map((option) => {
              const optionKey = `option_${option.toLowerCase()}`;
              const optionText = challenge[optionKey];
              const isSelected = selectedAnswer === option;
              const isCorrect = attempt?.is_correct === 1 && attempt?.selected_answer === option;

              if (!optionText) return null;

              return (
                <button
                  key={option}
                  onClick={() => {
                    // Não permitir mudança se já foi respondido corretamente ou capítulo completo
                    if (attempt?.is_correct !== 1 && !chapterCompleted) {
                      setSelectedAnswer(option);
                    }
                  }}
                  disabled={submitting || feedback?.type === 'success' || attempt?.is_correct === 1 || chapterCompleted}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? isCorrect
                        ? 'border-green-500 bg-green-50'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  } ${(submitting || feedback?.type === 'success' || attempt?.is_correct === 1) ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        isSelected
                          ? isCorrect
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {option}
                    </div>
                    <span className="text-gray-800">{optionText}</span>
                    {isCorrect && (
                      <FiCheckCircle className="ml-auto text-green-600 text-xl" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Essay Answer Textarea */}
        {!isMultipleChoice && (
          <div className="mb-6">
            {/* Mostrar nota e feedback se já foi corrigido */}
            {attempt?.is_graded === 1 && (
              <div className={`mb-4 p-4 rounded-lg border-2 ${
                attempt.grade >= 60 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className={`font-semibold ${
                    attempt.grade >= 60 ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {attempt.grade >= 60 ? '✓ Aprovado' : '✗ Reprovado'}
                  </h4>
                  <span className={`text-lg font-bold ${
                    attempt.grade >= 60 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    Nota: {attempt.grade}/100
                  </span>
                </div>
                {attempt.feedback && (
                  <div className={`mt-2 p-3 rounded ${
                    attempt.grade >= 60 ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <p className={`text-sm ${
                      attempt.grade >= 60 ? 'text-green-900' : 'text-red-900'
                    }`}>
                      <strong>Feedback:</strong> {attempt.feedback}
                    </p>
                  </div>
                )}
                {attempt.grade < 60 && (
                  <p className="text-sm text-red-700 mt-2 font-medium">
                    Você pode reenviar uma nova resposta abaixo.
                  </p>
                )}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sua Resposta:
            </label>
            <textarea
              value={essayAnswer || ''}
              onChange={(e) => {
                // Permitir edição se:
                // - Não foi aprovado (grade < 60 ou não foi corrigido ainda)
                // - Capítulo não está completo OU questão foi reprovada (pode reenviar)
                const isApproved = attempt?.is_graded === 1 && attempt?.grade >= 60;
                const canEdit = !isApproved && (!chapterCompleted || (attempt?.is_graded === 1 && attempt?.grade < 60));
                if (canEdit) {
                  setEssayAnswer(e.target.value);
                }
              }}
              disabled={submitting || (attempt?.is_graded === 1 && attempt?.grade >= 60) || (chapterCompleted && !(attempt?.is_graded === 1 && attempt?.grade < 60))}
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder={essayAnswer ? essayAnswer : "Digite sua resposta aqui..."}
            />
            {!chapterCompleted && (
              <p className="mt-2 text-sm text-gray-500">
                <FiClock className="inline mr-1" />
                {attempt?.is_graded === 1 && attempt?.grade >= 60
                  ? 'Esta resposta foi aprovada. Não é possível reenviar.'
                  : attempt?.is_graded === 1 && attempt?.grade < 60
                  ? 'Esta resposta foi reprovada. Você pode reenviar uma nova resposta.'
                  : 'Esta resposta será revisada por um professor. Você receberá feedback após a correção.'}
              </p>
            )}
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              feedback.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : feedback.needsGrading
                ? 'bg-blue-50 text-blue-800 border border-blue-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {feedback.type === 'success' ? (
              <FiCheckCircle className="text-2xl flex-shrink-0 mt-0.5" />
            ) : feedback.needsGrading ? (
              <FiFileText className="text-2xl flex-shrink-0 mt-0.5" />
            ) : (
              <FiXCircle className="text-2xl flex-shrink-0 mt-0.5" />
            )}
            <p className="font-medium">{feedback.message}</p>
          </div>
        )}

        {/* Submit Button */}
        {(() => {
          // Se múltipla escolha já foi respondida corretamente, não mostrar botão
          if (isMultipleChoice && attempt?.is_correct === 1) {
            return (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <FiCheckCircle className="text-green-600 text-xl" />
                  <p className="text-green-800 font-medium">Você já respondeu corretamente esta questão!</p>
                </div>
                <p className="text-sm text-green-700 mt-2">Sua resposta: <strong>{attempt.selected_answer}</strong></p>
              </div>
            );
          }
          
          // Se discursiva foi aprovada, não mostrar botão
          if (!isMultipleChoice && attempt?.is_graded === 1 && attempt?.grade >= 60) {
            return (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <FiCheckCircle className="text-green-600 text-xl" />
                  <p className="text-green-800 font-medium">Resposta aprovada! Não é necessário reenviar.</p>
                </div>
              </div>
            );
          }
          
          // Se discursiva foi reprovada, mostrar botão de reenvio
          if (!isMultipleChoice && attempt?.is_graded === 1 && attempt?.grade < 60) {
            return (
              <button
                onClick={handleSubmit}
                disabled={submitting || !essayAnswer.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Enviando...' : 'Reenviar Resposta'}
              </button>
            );
          }
          
          // Para outras situações (questão não respondida ou múltipla escolha não respondida corretamente)
          if (!chapterCompleted || (!isMultipleChoice && (!attempt || !attempt.is_graded || attempt.grade < 60))) {
            return (
              <button
                onClick={handleSubmit}
                disabled={
                  submitting || 
                  (isMultipleChoice && !selectedAnswer) || 
                  (!isMultipleChoice && !essayAnswer.trim())
                }
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  'Enviando...'
                ) : feedback?.type === 'success' ? (
                  <>
                    <FiPlay />
                    Próximo Desafio
                  </>
                ) : (
                  'Enviar Resposta'
                )}
              </button>
            );
          }
          
          // Se chegou aqui, não mostrar botão (questão já completa e capítulo completo)
          return null;
        })()}

        {feedback?.chapterCompleted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <FiCheckCircle className="text-4xl text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-green-800 mb-2">
              Parabéns! Capítulo Concluído!
            </h3>
            <p className="text-green-600 mb-4">
              Você completou todos os desafios deste capítulo. O próximo capítulo foi desbloqueado!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeView;
